import { VOLUNTEERING_WEEK } from '../constants/volunteeringWeek.ts'
import { ProjectType } from '../types/Project.ts'
import { VisibilityType } from '../types/visibility.ts'
import {
    getVolunteeringWeekEndBoundary,
    isWithinVolunteeringWeekRange,
    isWithinVolunteeringWeekVisibilityWindow
} from './volunteeringWeekDates.ts'

const PUBLISHED_PROJECT_FILTER = 'visibility:=published'
const PUBLISHED_PROJECT_SEARCH_FILTER = 'visibility:published'
const DRAFT_VOLUNTEERING_WEEK_PROJECT_FILTER = `visibility:=draft && topics:=${VOLUNTEERING_WEEK.TOPIC}`
const DRAFT_VOLUNTEERING_WEEK_PROJECT_SEARCH_FILTER = `visibility:draft AND topics:${VOLUNTEERING_WEEK.TOPIC}`
const VOLUNTEERING_WEEK_TOPIC_FILTER = `topics:!=${VOLUNTEERING_WEEK.TOPIC}`

export function shouldHideVolunteeringWeekFromPublicProjectListings(now: Date = new Date()): boolean {
    return !isWithinVolunteeringWeekVisibilityWindow(now)
}

export function shouldShowDraftVolunteeringWeekProjectsInPublicListings(now: Date = new Date()): boolean {
    return isWithinVolunteeringWeekVisibilityWindow(now)
}

export function getPublicProjectListingFilter(now: Date = new Date()): string {
    if (shouldShowDraftVolunteeringWeekProjectsInPublicListings(now)) {
        return `(${PUBLISHED_PROJECT_FILTER} || (${DRAFT_VOLUNTEERING_WEEK_PROJECT_FILTER}))`
    }

    if (shouldHideVolunteeringWeekFromPublicProjectListings(now)) {
        return `${PUBLISHED_PROJECT_FILTER} && ${VOLUNTEERING_WEEK_TOPIC_FILTER}`
    }

    return PUBLISHED_PROJECT_FILTER
}

export function getPublicProjectSearchFilter(now: Date = new Date()): string {
    if (shouldShowDraftVolunteeringWeekProjectsInPublicListings(now)) {
        return `(${PUBLISHED_PROJECT_SEARCH_FILTER} OR (${DRAFT_VOLUNTEERING_WEEK_PROJECT_SEARCH_FILTER}))`
    }

    if (shouldHideVolunteeringWeekFromPublicProjectListings(now)) {
        return `${PUBLISHED_PROJECT_SEARCH_FILTER} AND ${VOLUNTEERING_WEEK_TOPIC_FILTER}`
    }

    return PUBLISHED_PROJECT_SEARCH_FILTER
}

export function combinePublicProjectListingFilters(filters?: string, now: Date = new Date()): string {
    const publicFilter = getPublicProjectListingFilter(now)

    return filters ? `${publicFilter} && (${filters})` : publicFilter
}

export function isPublicVolunteeringWeekDraftProject(project: any, now: Date = new Date()): boolean {
    if (project?.type !== ProjectType.VOLUNTEERING_WEEK || project?.visibility !== VisibilityType.draft) {
        return false
    }

    if (!shouldShowDraftVolunteeringWeekProjectsInPublicListings(now)) {
        return false
    }

    const startDate = toDate(project.startDateAt || project.when)

    return Boolean(startDate && isWithinVolunteeringWeekRange(startDate))
}

export function shouldAllowDraftVolunteeringWeekOpportunities(now: Date = new Date()): boolean {
    return now <= getVolunteeringWeekEndBoundary()
}

function toDate(value: any): Date | null {
    if (!value) return null
    if (value instanceof Date) return value
    if (typeof value?.toDate === 'function') return value.toDate()
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }
    if (typeof value?._seconds === 'number') {
        return new Date(value._seconds * 1000)
    }
    if (typeof value?.seconds === 'number') {
        return new Date(value.seconds * 1000)
    }
    return null
}
